import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, InlineNotification } from '@carbon/react';
import { ShoppingCart, ArrowLeft, Checkmark, Delivery, Return } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useProduct } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';
import { toWebpImageUrl } from '../utils/imageUrl';
import { getProductVariants } from '../utils/productVariants';
import ProductGallery from '../components/ProductGallery';
import { buildCartItem, isProductInStock } from '../utils/cartItem';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { addToCart } = useCart();
  const [selectedVariant, setSelectedVariant] = useState('Black'); // For KBeacon variants
  const [addedToCart, setAddedToCart] = useState(false);

  // Fetch product from Supabase
  const { product, isLoading, error } = useProduct(id);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Format category name
  const formatCategoryName = (category) => {
    if (!category) return 'General';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Format specifications from product.specifications
  const getSpecs = () => {
    if (!product?.specifications) return [];
    
    const specs = typeof product.specifications === 'string' 
      ? JSON.parse(product.specifications) 
      : product.specifications;
    
    return Object.entries(specs).map(([key, value]) => ({
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: String(value)
    }));
  };

  // Get benefits based on sensor type (matching React Native)
  const getBenefits = () => {
    if (!product) return [];
    
    switch (product.sensor_type) {
      case 'kbeacon':
        return [
          'Bluetooth 5.0 connectivity',
          '2+ years battery life',
          'High accuracy readings',
          'Real-time monitoring',
          'Easy setup and pairing'
        ];
      case 'ela':
        return [
          'Compact design',
          'Energy efficient',
          'Reliable temperature monitoring',
          'Long battery life',
          'Simple installation'
        ];
      case 'gateway':
        return [
          'WiFi connectivity',
          'Remote monitoring',
          'Supports multiple sensors',
          'Cloud data storage',
          'Mobile alerts'
        ];
      case 'probe':
        return [
          'High precision measurements',
          'Durable construction',
          'Waterproof design',
          'Wide temperature range',
          'Easy installation'
        ];
      case 'bundle':
        return [
          'Complete monitoring solution',
          'Significant cost savings',
          'Everything included',
          'Quick setup guide',
          'Premium support'
        ];
      default:
        return [];
    }
  };

  const handleAddToCart = () => {
    if (!product || !isProductInStock(product)) return;

    const variant = product.name.toLowerCase().includes('kbeacon') ? selectedVariant : undefined;
    const cartProduct = buildCartItem(product, { variant });
    if (!cartProduct) return;

    addToCart(cartProduct);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleContactSales = () => {
    if (!product) {
      navigate('/contact', { state: { scrollToForm: true, subject: 'sales' } });
      return;
    }
    const variantNote = product.name.toLowerCase().includes('kbeacon')
      ? ` (${selectedVariant} variant)`
      : '';
    navigate('/contact', {
      state: {
        scrollToForm: true,
        subject: 'sales',
        productName: product.name,
        message: `I'm interested in ${product.name}${variantNote}. Please contact me with pricing and availability.`,
      },
    });
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ color: textSecondary }}>Loading product...</p>
            </div>
          </Column>
        </Grid>
      </div>
    );
  }

  // Error or not found state
  if (error || !product) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ color: textSecondary, marginBottom: '16px' }}>Product not found</p>
              <Button kind="primary" onClick={() => navigate('/shop')}>
                Back to Shop
              </Button>
            </div>
          </Column>
        </Grid>
      </div>
    );
  }

  const specs = getSpecs();
  const benefits = getBenefits();
  const inStock = isProductInStock(product);

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="product-page" className="product-page">
      <style>{`
        button[data-testid="back-to-shop"] {
          color: #ffffff !important;
        }
        button[data-testid="back-to-shop"]:hover,
        button[data-testid="back-to-shop"]:active,
        button[data-testid="back-to-shop"]:focus {
          color: #ffffff !important;
          background-color: #002d9c !important;
        }
      `}</style>
      {addedToCart && (
        <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999 }}>
          <InlineNotification
            kind="success"
            title="Added to cart!"
            subtitle="Product has been added to your cart"
            hideCloseButton
            style={{ maxWidth: '300px' }}
          />
        </div>
      )}
      {/* Breadcrumb — matches Shop header blue */}
      <section style={{ backgroundColor: '#0f62fe', padding: '16px 0' }} className="product-breadcrumb-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Button
              kind="ghost"
              renderIcon={ArrowLeft}
              onClick={() => navigate('/shop')}
              data-testid="back-to-shop"
              style={{ color: '#ffffff' }}
            >
              Back to Shop
            </Button>
          </Column>
        </Grid>
      </section>

      {/* Product Details */}
      <section style={{ padding: '48px 0' }} className="product-details-section">
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '24px',
                textAlign: 'center',
              }}
            >
              {(() => {
                // Get product variants for gallery
                const originalUrl = product.image_url || product.image;
                const imageUrl = toWebpImageUrl(originalUrl);
                const variants = getProductVariants(product.name, imageUrl);
                
                // Debug logging for all products with variants
                if (variants.hasMultiple) {
                  console.log('Product with Variants Debug:', {
                    productName: product.name,
                    originalUrl,
                    imageUrl,
                    variants: variants.variants,
                    hasMultiple: variants.hasMultiple,
                    isWhite: product.name.toLowerCase().includes('white'),
                    isBlack: product.name.toLowerCase().includes('black'),
                    isKBeacon: product.name.toLowerCase().includes('kbeacon'),
                    isSensor: product.name.toLowerCase().includes('sensor')
                  });
                }
                
                return (
                  <ProductGallery
                    images={variants.variants}
                    productName={product.name}
                    style={{ maxWidth: '100%' }}
                  />
                );
              })()}
            </Tile>
          </Column>
          
          <Column lg={8} md={4} sm={4}>
            <div style={{ padding: '0 24px' }} className="product-info-container">
              {/* Badges - fix alignment */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }} className="product-badges">
                {product.featured && (
                  <Tag type="red">Featured</Tag>
                )}
                <Tag type="blue">{formatCategoryName(product.category)}</Tag>
              </div>
              <h1 style={{ color: textPrimary, fontSize: '2.5rem', fontWeight: 600, marginBottom: '16px' }} data-testid="product-name">
                {product.name}
              </h1>
              
              <div style={{ marginBottom: '24px' }}>
                <span style={{ color: '#0f62fe', fontSize: '2.5rem', fontWeight: 600 }} data-testid="product-price">
                  {formatPrice(product.price)}
                </span>
                <Tag type={inStock ? 'green' : 'red'} style={{ marginLeft: '16px' }}>
                  {inStock ? 'In Stock' : 'Out of Stock'}
                </Tag>
              </div>
              
              {/* Match React Native: Display description prominently after price, like ProductDetailScreen line 631 */}
              {product.description && (
                <div style={{ 
                  color: textSecondary, 
                  marginBottom: '24px', 
                  lineHeight: 1.6, 
                  fontSize: '1rem',
                  fontWeight: '400',
                  whiteSpace: 'pre-wrap', // Preserve line breaks and formatting
                  wordWrap: 'break-word'
                }}>
                  {product.description}
                </div>
              )}
              {!product.description && (
                <p style={{ 
                  color: textSecondary, 
                  marginBottom: '24px', 
                  lineHeight: 1.6, 
                  fontSize: '1rem', 
                  fontStyle: 'italic' 
                }}>
                  No description available for this product.
                </p>
              )}

              {/* Variant Selector for KBeacon products - matching React Native */}
              {product.name.toLowerCase().includes('kbeacon') && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  marginBottom: '24px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ color: textSecondary, fontSize: '1rem', fontWeight: '500' }}>Color:</span>
                  {['Black', 'White'].map((variant) => (
                    <button
                      key={variant}
                      onClick={() => setSelectedVariant(variant)}
                      style={{
                        padding: '8px 12px',
                        border: 'none',
                        borderBottom: `2px solid ${selectedVariant === variant ? '#0f62fe' : 'transparent'}`,
                        backgroundColor: 'transparent',
                        color: selectedVariant === variant ? '#0f62fe' : textSecondary,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {variant}
                    </button>
                  ))}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>
                <Button 
                  kind="primary" 
                  size="lg" 
                  type="button"
                  renderIcon={ShoppingCart} 
                  disabled={!inStock} 
                  onClick={handleAddToCart}
                  data-testid="add-to-cart"
                >
                  {addedToCart ? 'Added!' : 'Add to Cart'}
                </Button>
                <Button kind="secondary" size="lg" type="button" onClick={handleContactSales} data-testid="contact-sales">
                  Contact Sales
                </Button>
              </div>
              
              <div style={{ display: 'flex', gap: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textSecondary }}>
                  <Delivery size={20} style={{ color: '#0f62fe' }} /> Free shipping
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textSecondary }}>
                  <Return size={20} style={{ color: '#0f62fe' }} /> 30-day returns
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Key Benefits, Specifications, and Compatibility - Match React Native inline layout */}
      <section style={{ backgroundColor: bgSecondary, padding: '48px 0' }} className="product-specs-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ padding: '0 24px' }}>
              {/* Key Benefits - matching React Native */}
              {benefits.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.125rem', fontWeight: '600' }}>
                    Key Benefits
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {benefits.map((benefit, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', color: textSecondary }}>
                        <Checkmark size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ fontSize: '0.875rem' }}>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Specifications - matching React Native table format */}
              {specs.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.125rem', fontWeight: '600' }}>
                    Specifications
                  </h3>
                  <div style={{ backgroundColor: 'transparent', padding: 0 }}>
                    {specs.map((spec, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingVertical: '8px',
                          paddingHorizontal: '16px',
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <span style={{ color: textSecondary, fontSize: '0.875rem', fontWeight: '500', textTransform: 'uppercase', flex: 1 }}>
                          {spec.label.replace(/_/g, ' ')}
                        </span>
                        <span style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: '500', textAlign: 'right', flex: 1 }}>
                          {String(spec.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Compatibility - matching React Native */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.125rem', fontWeight: '600' }}>
                  Compatibility
                </h3>
                <p style={{ color: textSecondary, lineHeight: 1.6, fontSize: '0.875rem' }}>
                  Works with this Chill Verify app on iOS and Android devices. 
                  Requires Bluetooth 4.0 or later for sensor connectivity.
                </p>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: bgPrimary, padding: '80px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '16px' }}>
                Need Help Choosing?
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px' }}>
                Our team is here to help you find the perfect solution
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Button kind="primary" type="button" onClick={handleContactSales} data-testid="product-contact">
                  Contact Us
                </Button>
                <Button kind="secondary" onClick={() => navigate('/shop')} data-testid="browse-products">
                  Browse All Products
                </Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default ProductPage;
